import { Empty, Note, Panel } from "./Panel";
import { PnLShape } from "./PnLShape";
import { RateCell, RateHead } from "./RateCell";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import { usePairSummary } from "../hooks/usePair";
import type { ShadowShape } from "../api/types";
import { MIN_SIGNALS, roleLabel, trustworthy, verdict, view, type PairView } from "../lib/pair";

/**
 * Пара дешевле доллара.
 *
 * # Зачем панель на экране
 *
 * Пара Up + Down гасится РОВНО в доллар — выигрывает один из двух, — поэтому
 * купивший обе ноги дешевле доллара забирает разницу независимо от исхода.
 * Перепись контрагентов назвала этот класс единственным, где сигнал наблюдаем
 * прямо у нас: сумма лучших предложений двух токенов есть наш собственный
 * стакан, а не чужой кошелёк.
 *
 * # Что панель обязана показать, и в каком порядке
 *
 * 1. НАБЛЮДАЛОСЬ против СОБРАЛОСЬ. Пара дешевле доллара на вершине в две share —
 *    не сделка, а показание прибора.
 * 2. КРОМКА рядом со СТОИМОСТЬЮ. Комиссия равна 0.07·p·(1−p) и максимальна у
 *    середины: пара 0.50/0.50 отдаёт 3.5 ¢ на доллар выплаты — весь арбитраж, —
 *    а пара 0.02/0.98 всего 0.28 ¢. Стоимость и объясняет, почему кромка живёт
 *    только у краёв цены.
 * 3. СНОС у гибрида. Мейкера наливают ровно тогда, когда рынок пошёл против
 *    него, и если снос больше кромки — роль закрывается этим числом.
 *
 * # Чего на панели НЕТ и не будет
 *
 * Кнопок. У подсистемы нет порта постановки ордера — не «выключен флагом», а нет
 * в зависимостях вовсе.
 */
export function PairPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = usePairSummary(hours, windowLen);
  const roles = summary.data?.roles ?? [];
  // Форма счёта — общим запросом на все журналы: границы корзин обязаны быть
  // одни, иначе гистограммы соседних панелей перестанут сравниваться.
  const shapes = useJournalShapes(hours, "pair", windowLen);

  return (
    <Panel
      title="Пара дешевле доллара"
      subtitle={summary.data ? verdict(roles) : undefined}
    >
      {!summary.data ? (
        <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : roles.length === 0 ? (
        <Empty>
          пар дешевле доллара ещё не было — и это ожидаемое состояние, а не
          поломка: комиссия у середины цены больше любой правдоподобной кромки
        </Empty>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">роль</th>
                  <th className="pb-2 text-right font-normal">наблюдалось</th>
                  <th className="pb-2 text-right font-normal">собралось</th>
                  <th className="pb-2 text-right font-normal">кромка</th>
                  <th className="pb-2 text-right font-normal">стоит пара</th>
                  <th className="pb-2 text-right font-normal">share</th>
                  <th className="pb-2 text-right font-normal">налито</th>
                  <th className="pb-2 text-right font-normal">снос</th>
                  <th className="pb-2 text-right font-normal">голая нога</th>
                  <th className="pb-2 text-right font-normal">попыток</th>
                  <RateHead />
                  <th className="pb-2 text-right font-normal">форма счёта</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <Row
                    key={r.role}
                    v={view(r)}
                    hours={hours}
                    shape={shapeOf(shapes, r.role)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* РАЗНЫЕ фолды, а не один: приговор ролям обновляется каждым окном
              сводки, а объяснение колонок не менялось ни разу с заведения
              панели. Свёрнутые в одну подсказку, они заставляли бы раскрывать
              правило ради ставки. */}
          <Note label="ставка и хрупкость — по ролям">
            <ul className="space-y-1">
              {roles.map((r) => {
                const shape = shapeOf(shapes, r.role);
                return (
                  <li key={r.role}>
                    <span className="num">{r.role}</span> — {roleLabel(r.role)};{" "}
                    <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                    <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>
                  </li>
                );
              })}
            </ul>
          </Note>
          <Note label="как читать: стоимость пары, снос и порог доверия">
            <ul className="space-y-1">
              <li>
                «стоит пара» — обе цены С КОМИССИЕЙ. Она равна 0.07·p·(1−p) на ноге
                и максимальна у середины: пара 0.50/0.50 отдаёт 3.5 ¢ на доллар
                выплаты, пара 0.02/0.98 — 0.28 ¢. Отсюда и весь арбитраж живёт у
                краёв цены.
              </li>
              <li>
                «снос» — насколько подорожала вторая нога, пока наливали первую.
                Снос больше кромки закрывает гибрид этим числом и больше ничем.
              </li>
              <li>
                Строки тусклее {MIN_SIGNALS} наблюдений читать нельзя. Ордеров за
                строками нет — у подсистемы нет порта постановки.
              </li>
            </ul>
          </Note>
        </>
      )}
    </Panel>
  );
}

function Row({
  v,
  shape,
  hours,
}: {
  v: PairView;
  shape: ShadowShape | undefined;
  hours: number;
}) {
  const r = v.row;
  const dim = trustworthy(r) ? "" : " text-[var(--color-muted)]";
  // Снос, съевший кромку, красится как отказ: это не «чуть хуже», а вердикт.
  const slipTone = v.slipEatsEdge === true ? "text-[var(--color-ask)]" : "";
  return (
    <tr className={`border-t border-[var(--color-border)]${dim}`}>
      <td className="py-1.5">{r.role}</td>
      <td className="py-1.5 text-right num">{r.signals}</td>
      <td className="py-1.5 text-right num">
        {r.feasible} <span className="text-[var(--color-muted)]">{pct(v.feasibleShare)}</span>
      </td>
      <td className={`py-1.5 text-right num ${tone(v.edge)}`}>{num(v.edge, 4)}</td>
      <td className="py-1.5 text-right num">{num(v.costNet, 4)}</td>
      <td className="py-1.5 text-right num">{num(v.shares, 1)}</td>
      <td className="py-1.5 text-right num">
        {r.watched > 0 ? `${r.filled}/${r.watched}` : "—"}
      </td>
      <td className={`py-1.5 text-right num ${slipTone}`}>{num(v.slip, 4)}</td>
      <td className={`py-1.5 text-right num ${tone(v.nakedPnL)}`}>{num(v.nakedPnL, 2)}</td>
      <td className="py-1.5 text-right num">{r.max_attempt}</td>
      {/* Удельный счёт — по ГОЛОЙ НОГЕ: форма и ставка считаются по naked_pnl,
          и ставка обязана стоять у того числа, из которого посчитана. Гашение
          передаётся отдельно, иначе inline-цвет минуса перебил бы тусклую
          строку. */}
      <RateCell shape={shape} hours={hours} dim={dim !== ""} />
      {/* Форма СЧЁТА голой ноги: сумма слева отвечает «сколько», гистограмма —
          «из чего». У пары это особенно важно: одна незакрытая нога теряет весь
          билет, и счёт складывается из редких −$25 против частых мелких плюсов. */}
      <td className="py-1.5 text-right">
        <PnLShape shape={shape} />
      </td>
    </tr>
  );
}

/** Доля процентами. Прочерк вместо нуля: «нечего делить» — не «ноль». */
function pct(v: number | null): string {
  return v === null ? "" : `${(100 * v).toFixed(0)} %`;
}

function num(v: number | null, digits: number): string {
  return v === null ? "—" : v.toFixed(digits);
}

/** Цвет знака. Ноль и прочерк нейтральны: ни то, ни другое не новость. */
function tone(v: number | null): string {
  if (v === null || v === 0) return "";
  return v > 0 ? "text-[var(--color-bid)]" : "text-[var(--color-ask)]";
}
